import { GatsbyPageContextProvider } from "@contexts/GatsbyPageContext";
import { withAllSDK } from "@contexts/withAllSDK";
import StaticPageLayout from "@globals/StaticPageLayout";
import { useIntegrations, useLightboxContext } from "@hooks";
import { useRouter } from "next/router";
import React, { useEffect, useRef } from "react";
import ActivateSim from "@components/ActivateForm/ActivationJourney";
import NextSteps from "@components/BuyForm/NextSteps";
import NavigationContainerProvider, {
	type NavigationProp,
	useNavigation,
} from "@contexts/NavigationContainer";
import { ActivateFlowStep, isEmptyContentfulEntry } from "@helpers";
import { getBaseUrl } from "@helpers/env";
import ContentService from "@utils/content-service";
import Head from "next/head";

import LightBox from "@components/BuyForm/LightBox";
import { mixpanelTrack } from "@helpers/mixpanel.helper";
import { GoogleAnalytics, GoogleTagManager } from "@next/third-parties/google";
import { useActivate } from "@contextsActivateContext";

const ActivateForm = ({ massRetailcarouselData }) => {
	const { routeName } = useNavigation() as NavigationProp;
	const lastTrackedRoute = useRef<string | null>(null);

	useEffect(() => {
		// Only track if routeName has actually changed and is not null/undefined
		if (routeName && String(routeName) !== String(lastTrackedRoute.current)) {
			mixpanelTrack("form_step_started", {
				form_step_name: routeName,
			});
			lastTrackedRoute.current = String(routeName);
		}
	}, [routeName]);

	if (routeName === ActivateFlowStep.NextSteps) {
		return <NextSteps />;
	}

	return <ActivateSim massRetailcarouselData={massRetailcarouselData} />;
};

interface TsAndCs {
	data: Record<string, Document>;
	content: Array<Record<string, any>>;
	nodeType: Document;
}

interface IndividualPlan {
	// Define the structure of individual plans if known
	[key: string]: any;
}

interface Fields {
	appStoreUrl: string;
	backgroundColour: string;
	googlePlayUrl: string;
	header: string;
	individualPlans: IndividualPlan[];
	name: string;
	nonePlanPage: string;
	promoBackgroundColour: string;
	promoCopy: string;
	promoIconRef: string;
	removeNegativeSpacingBelowModule: string;
	tsAndCs: TsAndCs;
}

interface Metadata {
	tags: any[];
}

interface Sys {
	contentType: { sys: Record<string, any> };
	createdAt: string;
	environment: { sys: Record<string, any> };
	id: string;
	locale: string;
	revision: number;
	space: { sys: Record<string, any> };
	type: string;
	updatedAt: string;
}

interface MassRetailCarouselData {
	metadata: Metadata;
	sys: Sys;
	fields: Fields;
}

interface PageProps {
	data: {
		fields: {
			pageMeta: {
				fields: {
					title?: string;
					description?: string;
					ogtitle?: string;
					ogdescription?: string;
					ogimageMedia?: { fields: { file: { url: string } } };
					keywords?: string;
					author?: string;
					ogurl?: string;
					oglocale?: string;
					ogimage?: string;
					ogtype?: string;
				};
			};
			[key: string]: any;
		};
	};
	massRetailcarouselData: MassRetailCarouselData;
}

const Page = ({ data, massRetailcarouselData }: PageProps) => {
	const { pageMeta } = data?.fields || {};

	const {
		title,
		description,
		ogtitle,
		ogdescription,
		ogimageMedia,
		keywords,
		author,
		ogurl,
		oglocale,
		ogimage,
		ogtype,
	} = pageMeta?.fields || {};

	const lightboxContext = useLightboxContext()?.lightboxContext;

	useActivate();

	useIntegrations();

	const router = useRouter();
	const path = router.pathname;
	const nextJsData = data?.fields;
	const pageContext = {
		appStoreBadge: nextJsData.appStoreBadge,
		appStoreLink: nextJsData.appStoreLink,
		eSimCompatibleDescription: nextJsData.eSimCompatibleDescription,
		eSimIncompatibleDescription: nextJsData.eSimIncompatibleDescription,
		eSimMaybeCompatibleDescription: nextJsData.eSimMaybeCompatibleDescription,
		eSimMoreInfo: nextJsData.eSimMoreInfo,
		faq: nextJsData.faq,
		headerImage: nextJsData.headerImage,
		name: nextJsData.name,
		pageMeta: nextJsData.pageMeta,
		plasticSimDescription: nextJsData.plasticSimDescription,
		plasticSimMoreInfo: nextJsData.plasticSimMoreInfo,
		playStoreBadge: nextJsData.playStoreBadge,
		playStoreLink: nextJsData.playStoreLink,
		qrCode: nextJsData.qrCode,
		relativeUrl: nextJsData.relativeUrl,
		setEmailHeader: nextJsData.setEmailHeader,
		setPlanHeader: nextJsData.setPlanHeader,
		setPlanLightboxForOneTreePlanted:
			nextJsData.setPlanLightboxForOneTreePlanted,
		setPlanLightboxForUnlimitedDataSpeeds:
			nextJsData.setPlanLightboxForUnlimitedDataSpeeds,
	};

	const noIndexUrls = [
		"/terms-policies/complaints-policy",
		"/terms-policies/privacy",
	];
	const isNoIndex = noIndexUrls.includes(ogurl ?? "");

	return (
		<>
			<Head>
				<GoogleTagManager gtmId={process.env.NEXT_PUBLIC_GTM_KEY || ""} />
				<title>{isEmptyContentfulEntry(title) ? "" : title}</title>
				<link
					rel="canonical"
					href={`${getBaseUrl()}${router.asPath.split("?")[0]}`}
				/>
				<meta
					name="facebook-domain-verification"
					content="umwvevf3vuew9ebyg5bb4hxqswovn9"
				/>
				{!isEmptyContentfulEntry(author) && (
					<meta name="author" content={author} />
				)}
				{!isEmptyContentfulEntry(description) && (
					<meta name="description" content={description} />
				)}
				{!isEmptyContentfulEntry(keywords) && (
					<meta name="keywords" content={keywords} />
				)}

				{!isEmptyContentfulEntry(ogtitle) && (
					<meta property="og:title" content={ogtitle} />
				)}
				{!isEmptyContentfulEntry(ogdescription) && (
					<meta property="og:description" content={ogdescription} />
				)}
				{!isEmptyContentfulEntry(ogurl) && (
					<meta property="og:url" content={ogurl} />
				)}
				{!isEmptyContentfulEntry(oglocale) && (
					<meta property="og:locale" content={oglocale} />
				)}
				{!isEmptyContentfulEntry(
					ogimageMedia?.fields?.file?.url || ogimage,
				) && (
					<meta
						property="og:image"
						content={ogimageMedia?.fields?.file?.url || ogimage}
					/>
				)}
				{!isEmptyContentfulEntry(ogtype) && (
					<meta property="og:type" content={ogtype} />
				)}

				{isNoIndex && <meta name="robots" content="noindex" />}
			</Head>
			<NavigationContainerProvider
				initialRouteName={ActivateFlowStep.ActivateSim}
				onEnter={() => {}}
				onExit={() => {}}
			>
				{[
					<GatsbyPageContextProvider
						key="gatsby-page-context-provider"
						path={path}
						target="_blank"
						{...pageContext}
					>
						<StaticPageLayout isActivateForm styles={undefined}>
							<ActivateForm massRetailcarouselData={massRetailcarouselData} />
							{lightboxContext ? <LightBox content={lightboxContext} /> : null}
						</StaticPageLayout>
					</GatsbyPageContextProvider>,
				]}
			</NavigationContainerProvider>
		</>
	);
};
export const getStaticProps = async () => {
	const client = ContentService.instance;

	try {
		const data = await client.getEntryById("1wzdVDRHRVWRku5Bup9m5Z");
		const massRetailcarouselData = await client.getEntryById(
			"3o7AFLFdJNxO12rwNObu3o",
		);

		return {
			props: {
				data,
				massRetailcarouselData,
			},
		};
	} catch (error) {
		console.error("Error fetching data:", error);
		return {
			props: {
				data: null,
			},
		};
	}
};

export default withAllSDK(Page);
